import React, { memo, useContext } from 'react';
import { SidebarNextContext } from '../SidebarNext/SidebarNextContext';
import { useFocusRing } from '../providers/useFocusRing/useFocusRing';

import { dataHooks, skins } from './constants';
import { classes, st } from './SidebarItemNext.st.css';
import Transition from '../Transition';
import Text from '../Text';

type HTMLTagName = keyof HTMLElementTagNameMap;

type AsButtonProps = {
  as?: 'button';
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

type AsAnchorProps = {
  as?: 'a';
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

type AsOtherTagProps = {
  as?: Exclude<HTMLTagName, 'a' | 'button'>;
} & React.HTMLAttributes<HTMLElement>;

type AsAnyComponentProps = {
  as?: React.ComponentType<any>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  [props: string]: any;
};

// FIXME: this will produce invalid intersections (e.g. `prefix` already is HTML attribute but HTML attributes cannot be `ReactNode`)
type SidebarItemButtonNextProps = (
  | AsButtonProps
  | AsAnchorProps
  | AsOtherTagProps
  | AsAnyComponentProps
) & {
  children?: React.ReactNode;
  dataHook?: string;
  className?: string;
  itemKey?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  role?: string;
  disabled?: boolean;
  isInQuickNavigation?: boolean; // FIXME: deprecate, this was not in types anyway
  onClick?: React.MouseEventHandler<HTMLElement>;
  depth?: 'first' | 'second' | 'third';
  depthDivider?: 'top' | 'full' | 'bottom';
};

const SidebarItemButtonNext = memo<SidebarItemButtonNextProps>(props => {
  const context = useContext(SidebarNextContext);
  const {
    skin,
    level,
    selectedPath,
    inert,
    parent,
    closeQuickNav,
    legacy,
    minimized,
  } = context;

  const {
    as = 'button',
    role = as === 'a' ? 'link' : 'button', // FIXME: check original code, this is weird
    // @ts-ignore
    href, // FIXME: from intersection
    dataHook,
    children,
    disabled,
    onClick,
    className,
    itemKey,
    suffix,
    prefix,
    isInQuickNavigation,
    ...asComponentProps
  } = props;

  const { isFocusVisible, focusProps } = useFocusRing();

  const Component = as as React.ComponentType<any>;
  const selected =
    !!selectedPath?.length && selectedPath[selectedPath.length - 1] === itemKey;
  const inSelection =
    itemKey !== undefined && !!selectedPath?.includes(itemKey);
  const htmlHref = disabled ? undefined : href;
  const tabIndex = inert ? -1 : 0;
  const textSize = level === 1 ? 'small' : 'tiny';

  const inContextMenu = parent === 'inContextMenu';

  const buttonClassName = st(
    classes.root,
    {
      selected: selected || inSelection,
      disabled,
      skin,
      inContextMenu,
      hasPrefix: !!prefix,
      level,
      legacy,
      isFocusVisible,
    },
    className,
  );

  const handleClick = (event: React.MouseEvent) => {
    if (!disabled) {
      closeQuickNav?.();
      // @ts-ignore
      onClick?.(event, itemKey); // FIXME: itemKey was not typed in props
    }
  };

  return (
    // @ts-ignore
    <Component
      {...focusProps}
      className={buttonClassName}
      data-hook={dataHook}
      type="button"
      role={role}
      href={htmlHref}
      disabled={href ? false : disabled}
      tabIndex={tabIndex}
      onClick={handleClick}
      aria-current={selected ? 'page' : undefined} // FIXME: no longer correct
      data-selected={selected}
      data-disabled={disabled}
      data-text-size={textSize}
      data-skin={skin}
      {...asComponentProps}
    >
      {level === 3 && !inContextMenu && <hr className={classes.divider} />}
      {prefix && (
        <span
          className={classes.prefix}
          data-hook={dataHooks.prefix}
          aria-hidden
        >
          {prefix}
        </span>
      )}
      <Transition
        className={classes.textWrapper}
        show={!minimized}
        exitAnimation={{
          fadeOut: {
            duration: 'fast01',
            easing: 'exitEasing',
          },
        }}
      >
        <Text
          dataHook={dataHooks.title}
          className={classes.text}
          size={textSize}
          weight="normal"
          secondary={skin === skins.light}
          light={skin === skins.dark}
          skin={disabled ? 'disabled' : 'standard'}
          showTooltip={false}
        >
          {children}
        </Text>
      </Transition>
      {!disabled && suffix && !minimized && (
        <div className={classes.suffixContainer} aria-hidden>
          {Array.isArray(suffix)
            ? suffix.slice(0, 2).map(renderSuffix)
            : renderSuffix(suffix, 0)}
        </div>
      )}
    </Component>
  );
});

const renderSuffix = (suffix: React.ReactNode, index: number) => (
  <span
    key={`${dataHooks.suffix}-${index}`}
    data-hook={dataHooks.suffix}
    className={classes.suffix}
  >
    {suffix}
  </span>
);

SidebarItemButtonNext.displayName = 'SidebarItemButtonNext';

export default SidebarItemButtonNext;
